import { Component, Input, OnInit } from '@angular/core';
import { AuthService } from '@app/@core/services/auth.service';
import { HttpService } from '@app/@core/services/http.service';
import { PermissionStore } from '@app/@core/store/permission.store';
import { environment } from '@env/environment';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs';
// import { InboxItem } from './inbox-listing/inbox-listing.component';
// import { MessageDetails } from './message-details/message-details.component';
// import { SentItem } from './sent-listing/sent-listing.component';
// import { DraftItem } from './draft-listing/draft-listing.component';
// import { ArchiveItem } from './archive-listing/archive-listing.component';
import { InAppMessageService } from './in-app-message.service';
// import { InAppMessageService as PicsInAppMessageService } from '../../../../../../wy-pics-web/projects/pics-core/dynamic/src/lib/pics-dynamic/@core/service/case-inbox.service';
import { 
  AssociatedUser, 
  InAppMessage, 
  LookupItem,
  MessageQueryParams,
  MessageType,
  ModalMode,
  NewMessageData,
  Recipient
} from './in-app-message.types';
import { ActivatedRoute } from '@angular/router';
import { AlertService } from '@app/@core/services/alert.service';
import { SocketAdapterService } from '@app/@core/services/socket-adapter.server';
import { ArchiveItem, DataStoreService as PicsInAppDataStore, DraftItem, InboxItem, MessageDetails, SentItem } from '@pics-core/in-app-messaging';
import { HttpClient } from '@angular/common/http';

// Updated interface to match the actual API response from user service
interface CaseUserResponse {
  applicationId: string;
  users: AssociatedUser[];
  totalCount: number;
}

@Component({
  standalone: false,
  selector: 'in-app-message',
  templateUrl: './in-app-message.component.html',
  styleUrls: ['./in-app-message.component.scss']
})
export class InAppMessageComponent implements OnInit {
  // When used as an embedded component (e.g. inside Message Center) these inputs
  // override the values normally taken from the route param / session storage.
  @Input() applicationId?: number | string;
  @Input() userId?: number;
  // Global inbox mode: show all of the user's messages across every application
  // (no entityId sent). Read-only in this mode.
  @Input() allMessages = false;

  RBACORG: any = true;
  PERMISSION: any;
  orgSubs: Subscription;
  caseInboxEvent: BehaviorSubject<any>;
  
  // UI State
  activeTab = 'inbox';
  searchQuery = '';
  selectedInboxItemId: string | null = null;
  selectedMessage: MessageDetails | null = null;
  
  // Current items for each tab
  currentSentItems: SentItem[] = [];
  currentDraftItems: DraftItem[] = [];
  currentArchivedItems: ArchiveItem[] = [];

  isApplicant: boolean = false;
  
  // New Message Modal
  showNewMessageModal = false;
  prepopulatedMessageData: NewMessageData | null = null;
  messageModalMode: ModalMode = 'new';
  editingMessageId: string | null = null; // Track the message being edited
  
  // Case Users for New Message
  caseUsers: Recipient[] = [];
  isLoadingCaseUsers = false;
  
  // TODO: This should come from the current case context
  // For now, using a default application ID - this should be dynamic
  currentApplicationId: any; // This should be set based on the current case/application context

  // Priority data from API
  priorityOptions: LookupItem[] = [];
  isLoadingPriorityData = false;
  ActivateReadOnlyMode: boolean = false;
  private socketSub!: Subscription;
  private searchSub?: Subscription;
  private searchSubject = new Subject<string>();
  private lastAppliedSearchQuery = '';
  private messageRequestVersion = 0;

  /**
   * Get current user ID from session storage
   * @returns Current user ID or null if not found
   */
  private getCurrentUserId(): number | null {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      const currentUserRole = currentUser && currentUser['userroles'] && currentUser['userroles'][0] && currentUser['userroles'][0].name;
      this.isApplicant = currentUserRole && currentUserRole?.trim() === "Applicant" ? true : false;
      // Embedded usage: prefer explicitly provided userId over session storage.
      if (this.userId != null) {
        return this.userId;
      }
      const currentUserId = currentUser.id || currentUser.userId || currentUser.user_id;
      return currentUserId ? parseInt(currentUserId.toString()) : null;
    } catch (error) {
      console.error('Error parsing user from session storage:', error);
      return this.userId != null ? this.userId : null;
    }
  }

  /**
   * Get current user as a Recipient (for applicant auto-select in New Message).
   */
  private getCurrentUserAsRecipient(): Recipient | null {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      const id = currentUser.id ?? currentUser.userId ?? currentUser.user_id;
      if (id == null) return null;
      const name = [currentUser.firstname, currentUser.lastname].filter(Boolean).join(' ') || currentUser.name || 'Applicant';
      const role = currentUser?.userroles?.[0]?.name || 'Applicant';
      const initials = this.getInitials(name);
      return {
        id: String(id),
        name,
        role,
        email: currentUser.email || null,
        avatar: null,
        initials: initials || null
      };
    } catch {
      return null;
    }
  }


  /**
   * Build common query parameters for message requests
   * @returns MessageQueryParams object with common parameters
   */
  private buildMessageQueryParams(): MessageQueryParams | null {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      console.error('Current user ID not found in session storage');
      return null;
    }

    // Global inbox: return all of the user's messages, no application scoping.
    if (this.allMessages) {
      return {
        page: this.currentPage,
        limit: this.pageSize,
        search: this.searchQuery || undefined,
        userId: currentUserId
      };
    }

    if (this.currentApplicationId == null || this.currentApplicationId === '') {
      return null;
    }
    const entityId = parseInt(this.currentApplicationId, 10);
    if (isNaN(entityId)) {
      return null;
    }
    return {
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchQuery || undefined,
      entityId,
      userId: currentUserId
    };
  }

  // Tab data
  tabs = [
    { id: 'inbox', label: 'Inbox', count: 0 },
    { id: 'sent', label: 'Sent', count: 0 },
    { id: 'draft', label: 'Draft', count: 0 },
    { id: 'archive', label: 'Archive', count: 0 }
  ];

  // API Data
  inboxMessages: InAppMessage[] = [];
  sentMessages: InAppMessage[] = [];
  draftMessages: InAppMessage[] = [];
  archivedMessages: InAppMessage[] = [];
  
  // Loading states
  isLoadingInbox = false;
  isLoadingSent = false;
  isLoadingDraft = false;
  isLoadingArchive = false;
  
  // Loading more states for infinite scroll
  isLoadingMoreInbox = false;
  isLoadingMoreSent = false;
  isLoadingMoreDraft = false;
  isLoadingMoreArchive = false;
  
  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalMessages = 0;
  
  // Infinite scroll pagination for each tab
  inboxPagination = {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasMore: true,
    isLoading: false,
    hasScrolled: false // Track if user has scrolled and made API calls
  };
  
  sentPagination = {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasMore: true,
    isLoading: false,
    hasScrolled: false // Track if user has scrolled and made API calls
  };
  
  draftPagination = {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasMore: true,
    isLoading: false,
    hasScrolled: false // Track if user has scrolled and made API calls
  };
  
  archivePagination = {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasMore: true,
    isLoading: false,
    hasScrolled: false // Track if user has scrolled and made API calls
  };
  
  
  // Inbox items data - populated from API
  currentInboxItems: InboxItem[] = [];
  
  // Current displayed data - populated from API
  currentData: any[] = [];

  // Message details data - populated from API
  messageDetailsData: { [key: string]: MessageDetails } = {};

  // Sent items data - populated from API
  sentItemsData: SentItem[] = [];

  // Draft items data - populated from API
  draftItemsData: DraftItem[] = [];

  // Unread count for badge display
  unreadCount = 0;

  constructor(
    private permissionStore: PermissionStore, 
    private authService: AuthService, 
    private httpService: HttpService,
    private InAppMessageService: InAppMessageService,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private socketAdapter: SocketAdapterService,
    private picsInAppDataStore: PicsInAppDataStore,
  ) {
    this.caseInboxEvent = new BehaviorSubject<any>(null);
    // Populate the @pics-core/in-app-messaging store so AttachmentsService has HTTP client for file uploads
    this.picsInAppDataStore.setData('HTTPSERVICE', this.httpService);
    this.orgSubs = this.authService.orgInfo.subscribe(o => {
      this.PERMISSION = this.permissionStore.state;
      this.RBACORG = {
        tokenKey: 'jwt-token',
        apiHost: environment.apiHost,
        others: window.sessionStorage,
        orgID: o ? o : window.sessionStorage['orgid'],
        environment: {
          mstrUsername: environment.mstrUsername,
          mstrPassword: environment.mstrPassword,
          mstrURL: environment.mstrURL,
          mstrProjectID: environment.mstrProjectID,
          applicationId: environment.applicationid,
          priority: environment.priority,
          videoConferenceDomain: environment.videoConferenceDomain,
          ChatBotNew: environment.ChatBotNew,
          ChatBot: environment.ChatBot,
          n8nWorflowUrl: environment.n8nWorflowUrl,
          production: environment.production
        }
      };
      const payload = {
        RBACORG: this.RBACORG,
        PERMISSION: this.PERMISSION,
        HTTPSERVICE: this.httpService,
      };
      this.caseInboxEvent.next(payload);
      // Keep @pics-core/in-app-messaging store in sync so library services (e.g. AttachmentsService) have HTTP client
      this.picsInAppDataStore.setObject(payload);
    });
  }

  ngOnInit(): void {
    this.searchSub = this.searchSubject.pipe(
      debounceTime(300),
      map(searchText => (searchText || '').trim()),
      distinctUntilChanged()
    ).subscribe(searchText => {
      if (searchText === this.lastAppliedSearchQuery) {
        return;
      }

      this.applySearch(searchText);
    });

    this.socketSub = this.socketAdapter
      .receiveNewMessageRefresh()
      .subscribe(() => {
        if (this.currentApplicationId != null && this.currentApplicationId !== '') {
          this.loadMessages();
        }
      });

    // Global inbox: show all of the user's messages across applications (read-only).
    if (this.allMessages) {
      this.ActivateReadOnlyMode = true;
      this.loadPriorityData();
      this.loadMessages();
      return;
    }

    // Embedded usage: an applicationId input takes precedence over the route param.
    if (this.applicationId != null && this.applicationId !== '') {
      const applicationId = parseInt(this.applicationId.toString(), 10);
      if (!isNaN(applicationId)) {
        this.handleApplicationIdChange(applicationId);
        this.getUnreadCount(applicationId);
      }
      return;
    }

    this.route.params.subscribe(params => {
      const applicationIdParam = params['applicationId'];
      if (applicationIdParam) {
        const applicationId = parseInt(applicationIdParam, 10);
        if (!isNaN(applicationId)) {
          this.handleApplicationIdChange(applicationId);
          this.getUnreadCount(applicationId);
        }
      }
    });
  }

  /**
   * Load priority data from API
   */
  loadPriorityData(): void {
    console.log('Loading priority data from API...');
    this.isLoadingPriorityData = true;
    
    this.InAppMessageService.getPriorities().subscribe({
      next: (response) => {
        console.log('Priority data loaded:', response);
        // Handle both direct array response and nested data response
        this.priorityOptions =  (response as any)?.data;
        this.isLoadingPriorityData = false;
      },
      error: (error) => {
        console.error('Error loading priority data:', error);
        this.isLoadingPriorityData = false;
      }
    });
  }

  /**
   * Get unread count for the current application
   * Passes current user id so the API returns count for the logged-in user (required for correct count).
   */
  getUnreadCount(applicationId: number, userId?: number): void {
    const currentUserId = userId ?? this.getCurrentUserId() ?? undefined;
    this.InAppMessageService.getUnreadCount(applicationId, currentUserId).subscribe({
      next: (response) => {
        this.unreadCount = response.count;
      },
      error: (error) => {
        console.error('Error getting unread count:', error);
        this.unreadCount = 0;
      }
    });
  }

 

  // Load data based on active tab
  loadTabData(): void {
    switch (this.activeTab) {
      case 'inbox':
        this.currentData = [];
        this.currentSentItems = [];
        this.currentDraftItems = [];
        break;
      case 'sent':
        this.currentData = [];
        this.currentSentItems = [...this.sentItemsData];
        this.currentDraftItems = [];
        break;
      case 'draft':
        this.currentData = [];
        this.currentSentItems = [];
        this.currentDraftItems = [...this.draftItemsData];
        break;
      case 'archive':
        this.currentData = [];
        this.currentSentItems = [];
        this.currentDraftItems = [];
        this.updateCurrentArchivedItems();
        break;
      default:
        this.currentData = [];
        this.currentSentItems = [];
        this.currentDraftItems = [];
    }
  }

  // Load messages from API
  loadMessages(): void {
    const requestVersion = ++this.messageRequestVersion;
    this.loadInboxMessages(true, requestVersion);
    this.loadSentMessages(true, requestVersion);
    this.loadDraftMessages(true, requestVersion);
    this.loadArchivedMessages(true, requestVersion);
  }

  private loadActiveTabMessages(): void {
    const requestVersion = ++this.messageRequestVersion;

    switch (this.activeTab) {
      case 'inbox':
        this.loadInboxMessages(true, requestVersion);
        break;
      case 'sent':
        this.loadSentMessages(true, requestVersion);
        break;
      case 'draft':
        this.loadDraftMessages(true, requestVersion);
        break;
      case 'archive':
        this.loadArchivedMessages(true, requestVersion);
        break;
    }
  }

  // Handle scroll event for infinite loading
  onScroll(event: any): void {
    const element = event.target;
    const threshold = 100; // Load more when 100px from bottom
    
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      this.loadMoreMessages();
    }
  }

  // Load more messages for infinite scroll
  loadMoreMessages(): void {
    this.logPaginationState(this.activeTab);
    
    switch (this.activeTab) {
      case 'inbox':
        if (this.inboxPagination.hasMore && !this.inboxPagination.isLoading) {
          this.inboxPagination.hasScrolled = true; // Mark that user has scrolled
          this.loadInboxMessages(false); // Don't reset pagination
        } 
        break;
      case 'sent':
        if (this.sentPagination.hasMore && !this.sentPagination.isLoading) {
          this.sentPagination.hasScrolled = true; // Mark that user has scrolled
          this.loadSentMessages(false); // Don't reset pagination
        }
        break;
      case 'draft':
        if (this.draftPagination.hasMore && !this.draftPagination.isLoading) {
          this.draftPagination.hasScrolled = true; // Mark that user has scrolled
          this.loadDraftMessages(false); // Don't reset pagination
        } 
        break;
      case 'archive':
        if (this.archivePagination.hasMore && !this.archivePagination.isLoading) {
          this.archivePagination.hasScrolled = true; // Mark that user has scrolled
          this.loadArchivedMessages(false); // Don't reset pagination
        } 
        break;
    }
  }

  // Get current loading more state for active tab
  getCurrentLoadingMoreState(): boolean {
    switch (this.activeTab) {
      case 'inbox':
        return this.isLoadingMoreInbox;
      case 'sent':
        return this.isLoadingMoreSent;
      case 'draft':
        return this.isLoadingMoreDraft;
      case 'archive':
        return this.isLoadingMoreArchive;
      default:
        return false;
    }
  }

  // Get current pagination hasMore state for active tab
  getCurrentHasMoreState(): boolean {
    switch (this.activeTab) {
      case 'inbox':
        return this.inboxPagination.hasMore;
      case 'sent':
        return this.sentPagination.hasMore;
      case 'draft':
        return this.draftPagination.hasMore;
      case 'archive':
        return this.archivePagination.hasMore;
      default:
        return false;
    }
  }

  // Debug method to log pagination state
  logPaginationState(tab: string): void {
    const pagination = this.getPaginationForTab(tab);

  }

  // Get pagination object for specific tab
  getPaginationForTab(tab: string): any {
    switch (tab) {
      case 'inbox':
        return this.inboxPagination;
      case 'sent':
        return this.sentPagination;
      case 'draft':
        return this.draftPagination;
      case 'archive':
        return this.archivePagination;
      default:
        return null;
    }
  }



  // Load inbox messages
  loadInboxMessages(resetPagination: boolean = true, requestVersion?: number): void {
    if (resetPagination) {
      this.inboxPagination.currentPage = 1;
      this.inboxPagination.hasMore = true;
      this.inboxMessages = [];
    }
    
    if ((!resetPagination && this.inboxPagination.isLoading) || !this.inboxPagination.hasMore) {
      return;
    }
    
    this.inboxPagination.isLoading = true;
    
    // Use different loading states for initial load vs loading more
    if (resetPagination) {
      this.isLoadingInbox = true;
    } else {
      this.isLoadingMoreInbox = true;
    }
    
    const params = this.buildMessageQueryParams();
    if (!params) {
      this.isLoadingInbox = false;
      this.isLoadingMoreInbox = false;
      this.inboxPagination.isLoading = false;
      return;
    }

    // Add pagination parameters
    params.page = this.inboxPagination.currentPage;
    params.limit = this.inboxPagination.pageSize;
    params.messageView = 'INBOX'
    this.InAppMessageService.getInboxMessages(params).subscribe({
      next: (response: any) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        const newMessages = response.data?.data || response.data || [];
        const totalCount = response.data?.count || response.count || 0;
        const totalPages = response.data?.totalPages || 0;
        
        if (resetPagination) {
          this.inboxMessages = newMessages;
        } else {
          this.inboxMessages = [...this.inboxMessages, ...newMessages];
        }
        
        this.inboxPagination.totalCount = totalCount;
        this.inboxPagination.totalPages = totalPages;
        this.tabs[0].count = totalCount; // Show total count
        
        // Use totalPages to determine if there are more messages
        this.inboxPagination.hasMore = this.inboxPagination.currentPage < totalPages;
        this.inboxPagination.currentPage++;
        
        this.isLoadingInbox = false;
        this.isLoadingMoreInbox = false;
        this.inboxPagination.isLoading = false;
        this.updateCurrentInboxItems();
      },
      error: (error) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        console.error('Error details:', error.error);
        this.inboxMessages = [];
        this.tabs[0].count = 0;
        this.inboxPagination.totalCount = 0;
        this.inboxPagination.totalPages = 0;
        this.inboxPagination.hasMore = false;
        this.isLoadingInbox = false;
        this.isLoadingMoreInbox = false;
        this.inboxPagination.isLoading = false;
        this.updateCurrentInboxItems();
      }
    });
  }

  // Load sent messages
  loadSentMessages(resetPagination: boolean = true, requestVersion?: number): void {
    if (resetPagination) {
      this.sentPagination.currentPage = 1;
      this.sentPagination.hasMore = true;
      this.sentMessages = [];
    }
    
    if ((!resetPagination && this.sentPagination.isLoading) || !this.sentPagination.hasMore) {
      return;
    }
    
    this.sentPagination.isLoading = true;
    
    // Use different loading states for initial load vs loading more
    if (resetPagination) {
      this.isLoadingSent = true;
    } else {
      this.isLoadingMoreSent = true;
    }
    
    const params = this.buildMessageQueryParams();
    if (!params) {
      this.isLoadingSent = false;
      this.isLoadingMoreSent = false;
      this.sentPagination.isLoading = false;
      return;
    }

    // Add pagination parameters
    params.page = this.sentPagination.currentPage;
    params.limit = this.sentPagination.pageSize;
    params.messageView = 'SENT'

    this.InAppMessageService.getInboxMessages(params).subscribe({
      next: (response: any) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        const newMessages = response.data?.data || response.data || [];
        const totalCount = response.data?.count || response.count || 0;
        const totalPages = response.data?.totalPages || 0;
        
        if (resetPagination) {
          this.sentMessages = newMessages;
        } else {
          this.sentMessages = [...this.sentMessages, ...newMessages];
        }
        
        this.sentPagination.totalCount = totalCount;
        this.sentPagination.totalPages = totalPages;
        this.tabs[1].count = totalCount; // Show total count
        
        // Use totalPages to determine if there are more messages
        this.sentPagination.hasMore = this.sentPagination.currentPage < totalPages;
        this.sentPagination.currentPage++;
        
        this.isLoadingSent = false;
        this.isLoadingMoreSent = false;
        this.sentPagination.isLoading = false;
        this.updateCurrentSentItems();
      },
      error: (error) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        console.error('Error loading sent messages:', error);
        this.sentMessages = [];
        this.tabs[1].count = 0;
        this.sentPagination.totalCount = 0;
        this.sentPagination.totalPages = 0;
        this.sentPagination.hasMore = false;
        this.isLoadingSent = false;
        this.isLoadingMoreSent = false;
        this.sentPagination.isLoading = false;
        this.updateCurrentSentItems();
      }
    });
  }

  // Load draft messages
  loadDraftMessages(resetPagination: boolean = true, requestVersion?: number): void {
    if (resetPagination) {
      this.draftPagination.currentPage = 1;
      this.draftPagination.hasMore = true;
      this.draftMessages = [];
    }
    
    if ((!resetPagination && this.draftPagination.isLoading) || !this.draftPagination.hasMore) {
      return;
    }
    
    this.draftPagination.isLoading = true;
    
    // Use different loading states for initial load vs loading more
    if (resetPagination) {
      this.isLoadingDraft = true;
    } else {
      this.isLoadingMoreDraft = true;
    }
    
    const params = this.buildMessageQueryParams();
    if (!params) {
      this.isLoadingDraft = false;
      this.isLoadingMoreDraft = false;
      this.draftPagination.isLoading = false;
      return;
    }

    // Add pagination parameters
    params.page = this.draftPagination.currentPage;
    params.limit = this.draftPagination.pageSize;
    params.messageView = 'DRAFT'

    this.InAppMessageService.getInboxMessages(params).subscribe({
      next: (response: any) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        const newMessages = response.data?.data || response.data || [];
        const totalCount = response.data?.count || response.count || 0;
        const totalPages = response.data?.totalPages || 0;
        
        if (resetPagination) {
          this.draftMessages = newMessages;
        } else {
          this.draftMessages = [...this.draftMessages, ...newMessages];
        }
        
        this.draftPagination.totalCount = totalCount;
        this.draftPagination.totalPages = totalPages;
        this.tabs[2].count = totalCount; // Show total count
        
        // Use totalPages to determine if there are more messages
        this.draftPagination.hasMore = this.draftPagination.currentPage < totalPages;
        this.draftPagination.currentPage++;
        
        this.isLoadingDraft = false;
        this.isLoadingMoreDraft = false;
        this.draftPagination.isLoading = false;
        this.updateCurrentDraftItems();
      },
      error: (error) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        console.error('Error loading draft messages:', error);
        this.draftMessages = [];
        this.tabs[2].count = 0;
        this.draftPagination.totalCount = 0;
        this.draftPagination.totalPages = 0;
        this.draftPagination.hasMore = false;
        this.isLoadingDraft = false;
        this.isLoadingMoreDraft = false;
        this.draftPagination.isLoading = false;
        this.updateCurrentDraftItems();
      }
    });
  }

  // Load archived messages
  loadArchivedMessages(resetPagination: boolean = true, requestVersion?: number): void {
    if (resetPagination) {
      this.archivePagination.currentPage = 1;
      this.archivePagination.hasMore = true;
      this.archivedMessages = [];
    }
    
    if ((!resetPagination && this.archivePagination.isLoading) || !this.archivePagination.hasMore) {
      return;
    }
    
    this.archivePagination.isLoading = true;
    
    // Use different loading states for initial load vs loading more
    if (resetPagination) {
      this.isLoadingArchive = true;
    } else {
      this.isLoadingMoreArchive = true;
    }
    
    const params = this.buildMessageQueryParams();
    if (!params) {
      this.isLoadingArchive = false;
      this.isLoadingMoreArchive = false;
      this.archivePagination.isLoading = false;
      return;
    }

    // Add pagination parameters
    params.page = this.archivePagination.currentPage;
    params.limit = this.archivePagination.pageSize;
    params.messageView = "ARCHIVED";

    this.InAppMessageService.getInboxMessages(params).subscribe({
      next: (response: any) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }
        console.log('Archived messages response:', response);
        const newMessages = response.data?.data || response.data || [];
        const totalCount = response.data?.count || response.count || 0;
        const totalPages = response.data?.totalPages || 0;
        
        if (resetPagination) {
          this.archivedMessages = newMessages;
        } else {
          this.archivedMessages = [...this.archivedMessages, ...newMessages];
        }
        
        this.archivePagination.totalCount = totalCount;
        this.archivePagination.totalPages = totalPages;
        this.tabs[3].count = totalCount; // Show total count
        
        // Use totalPages to determine if there are more messages
        this.archivePagination.hasMore = this.archivePagination.currentPage < totalPages;
        this.archivePagination.currentPage++;
        
        this.isLoadingArchive = false;
        this.isLoadingMoreArchive = false;
        this.archivePagination.isLoading = false;
        this.updateCurrentArchivedItems();
      },
      error: (error) => {
        if (requestVersion !== undefined && requestVersion !== this.messageRequestVersion) {
          return;
        }

        console.error('Error loading archived messages:', error);
        this.archivedMessages = [];
        this.tabs[3].count = 0;
        this.archivePagination.totalCount = 0;
        this.archivePagination.totalPages = 0;
        this.archivePagination.hasMore = false;
        this.isLoadingArchive = false;
        this.isLoadingMoreArchive = false;
        this.archivePagination.isLoading = false;
        this.updateCurrentArchivedItems();
      }
    });
  }


  // Update current inbox items from API data
  updateCurrentInboxItems(): void {
    if (!this.inboxMessages || !Array.isArray(this.inboxMessages)) {
      this.currentInboxItems = [];
      return;
    }

    this.currentInboxItems = [];
    console.log("inbox messages in parent",this.inboxMessages)
    for (let i = 0; i < this.inboxMessages.length; i++) {
      const message = this.inboxMessages[i];
      
      // Get sender name and initials
      let senderName = 'Unknown User';
      let senderInitials = 'UU';
      let senderRole = 'User'; // Default role
      
      if (message.sender) {
        senderName = `${message.sender.firstname} ${message.sender.lastname}`;
        senderInitials = this.getInitials(senderName);
        
        // Get sender role directly from the sender object (now populated by API)
        if ((message.sender as any).role) {
          senderRole = (message.sender as any).role;
        }
      }
      
      // Determine if message is new (unread) based on read status
      const isNew = !message['recipients'][0].isRead;
      
      // Set recipient ID to current user ID (since recipientId should be the current user)
      const currentUserId = this.getCurrentUserId();
      
      let recipientId: number | undefined;
      if (currentUserId) {
        recipientId = currentUserId;
      } else {
        console.warn(`Current user ID not available for message ${message.id}`);
      }
      
      this.currentInboxItems.push({
        id: message.id.toString(),
        avatar: senderInitials, // Always use initials for avatar
        senderName: senderName,
        role: senderRole, // Use actual role from API
        subject: message.subject,
        description: message.messageContent.substring(0, 100) + (message.messageContent.length > 100 ? '...' : ''),
        timestamp: this.formatTimestamp(message.updatedAt ?? message.insertedOn),
        isNew: isNew,
        isRead: message && message['recipients'] && message['recipients'][0] && message['recipients'][0].isRead || false,
        readAt: message.readAt,
        priority: message.priority,
        recipientId: recipientId
      });
    }
  }

  // Update current sent items from API data
  updateCurrentSentItems(): void {
    if (!this.sentMessages || !Array.isArray(this.sentMessages)) {
      this.currentSentItems = [];
      return;
    }

    this.currentSentItems = [];
    for (let i = 0; i < this.sentMessages.length; i++) {
      const message = this.sentMessages[i];
      
      // Get recipient name from the message data
      let recipientName = 'Unknown User';
      let recipientInitials = 'UU';
      let recipientRole = 'User'; // Default role
      
      if (message.recipients && message.recipients.length > 0) {
        // Get name from recipients array
        const firstRecipient = message.recipients[0];
        if (firstRecipient.name) {
          recipientName = firstRecipient.name;
          recipientInitials = this.getInitials(firstRecipient.name);
          recipientRole = firstRecipient.role || 'User'; // Get role from recipient
        }
      }
      
      // If multiple recipients, join their names
      if (message.recipients && message.recipients.length > 1) {
        console.log('recipients for send 1',message.recipients)
        const names = message.recipients.map(r => r.name || 'Unknown');
        recipientName = names.join(', ');
        // For multiple recipients, use initials of the first recipient
        if (message.recipients[0] && message.recipients[0].name) {
          recipientInitials = this.getInitials(message.recipients[0].name);
          recipientRole = message.recipients[0].role || 'User'; // Get role from first recipient
        }
      }

      // If no recipients found, try to fetch them from API
      if (recipientName === 'Unknown User' && message.id) {
        console.log('recipients for send 2', message.recipients)
        this.fetchMessageRecipients(message.id, (recipients) => {
          if (recipients && recipients.length > 0) {
            const firstRecipient = recipients[0];
            recipientName = firstRecipient.name || firstRecipient.fullName || 'Unknown User';
            recipientInitials = this.getInitials(recipientName);
            
            // Update the current item with correct recipient info
            const itemIndex = this.currentSentItems.findIndex(item => item.id === message.id.toString());
            if (itemIndex !== -1) {
              this.currentSentItems[itemIndex].recipientName = recipientName;
              this.currentSentItems[itemIndex].avatar = recipientInitials;
            }
          }
        });
      }

      this.currentSentItems.push({
        id: message.id.toString(),
        avatar: recipientInitials,
        recipientName: recipientName,
        role: recipientRole, // Use actual role from API
        subject: message.subject,
        description: message.messageContent ? (message.messageContent.substring(0, 100) + (message.messageContent.length > 100 ? '...' : '')) : '',
        timestamp: this.formatTimestamp(message.updatedAt ?? message.insertedOn),
        isRead: false, // TODO: Get read status from recipients table
        status: 'sent',
        priority: message.priority
      });
      console.log('latest current sent users',this.currentSentItems)
    }
  }

  // Update current draft items from API data
  updateCurrentDraftItems(): void {
    if (!this.draftMessages || !Array.isArray(this.draftMessages)) {
      this.currentDraftItems = [];
      return;
    }

    this.currentDraftItems = [];
    for (let i = 0; i < this.draftMessages.length; i++) {
      const message = this.draftMessages[i];
      
      // Get recipient name and initials
      let recipientName = 'Unknown User';
      let recipientInitials = 'UU';
      let recipientRole = 'User'; // Default role
      
      if (message.recipients && message.recipients.length > 0) {
        // Get name from recipients array
        const firstRecipient = message.recipients[0];
        if (firstRecipient.name) {
          recipientName = firstRecipient.name;
          recipientInitials = this.getInitials(firstRecipient.name);
          recipientRole = firstRecipient.role || 'User'; // Get role from recipient
        }
      }
      
      // If multiple recipients, join their names
      if (message.recipients && message.recipients.length > 1) {
        const names = message.recipients.map(r => r.name || 'Unknown');
        recipientName = names.join(', ');
        // For multiple recipients, use initials of the first recipient
        if (message.recipients[0] && message.recipients[0].name) {
          recipientInitials = this.getInitials(message.recipients[0].name);
          recipientRole = message.recipients[0].role || 'User'; // Get role from first recipient
        }
      }
      
      this.currentDraftItems.push({
        id: message.id.toString(),
        avatar: recipientInitials, // Always use initials for avatar
        recipientName: recipientName,
        role: recipientRole, // Use actual role from API
        subject: message.subject,
        description: message.messageContent.substring(0, 100) + (message.messageContent.length > 100 ? '...' : ''),
        timestamp: this.formatTimestamp(message.updatedAt ?? message.insertedOn),
        lastModified: this.formatTimestamp( message.updatedAt ?? message.insertedOn),
        hasAttachments: false, // TODO: Implement attachments later
        priority: message.priority
      });
    }
  }

  // Update current archived items from API data
  updateCurrentArchivedItems(): void {
    if (!this.archivedMessages || !Array.isArray(this.archivedMessages)) {
      this.currentArchivedItems = [];
      return;
    }

    this.currentArchivedItems = [];
    for (let i = 0; i < this.archivedMessages.length; i++) {
      const message = this.archivedMessages[i];
      
      // Get sender name and initials
      let senderName = 'Unknown User';
      let senderInitials = 'UU';
      let senderRole = 'User';
      
      if (message.sender) {
        senderName = `${message.sender.firstname} ${message.sender.lastname}`;
        senderInitials = this.getInitials(senderName);
        
        // Get sender role directly from the sender object (now populated by API)
        if ((message.sender as any).role) {
          senderRole = (message.sender as any).role;
        }
      }
      
      this.currentArchivedItems.push({
        id: message.id.toString(),
        avatar: senderInitials,
        senderName: senderName,
        role: senderRole,
        subject: message.subject,
        description: message.messageContent.substring(0, 100) + (message.messageContent.length > 100 ? '...' : ''),
        timestamp: this.formatTimestamp(message.updatedAt ?? message.insertedOn),
        priority: message.priority,
        archivedAt: message.updatedAt || message.insertedOn, // Use updatedAt as archived date
        originalMessageType: message.messageType // Keep original message type
      });
    }
  }

  // Format timestamp for display
  private formatTimestamp(date: Date): string {
    if (!date) {
      return 'Unknown';
    }

    try {
      const now = new Date();
      const messageDate = new Date(date);
      
      // Check if the date is valid
      if (isNaN(messageDate.getTime())) {
        return 'Invalid Date';
      }
      
      const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return messageDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Error';
    }
  }

  // Switch between tabs
  switchTab(tabId: string): void {
    this.activeTab = tabId;
    this.selectedInboxItemId = null; // Clear selected item when switching tabs
    this.selectedMessage = null; // Clear message details when switching tabs
    this.loadTabData();
    this.searchQuery = ''; // Clear search when switching tabs
    this.lastAppliedSearchQuery = '';
    this.currentPage = 1; // Reset to first page
    this.resetAllPagination();
    
    this.loadMessages(); // Reload messages for the new tab
  }

  onSearchChange(searchText: string): void {
    this.searchSubject.next(searchText);
  }

  // Handle search
  onSearch(): void {
    this.applySearch(this.searchQuery);
  }

  private applySearch(searchText: string): void {
    const normalizedSearch = (searchText || '').trim();
    this.searchQuery = normalizedSearch;
    this.lastAppliedSearchQuery = normalizedSearch;

    // Reload messages with search query
    this.currentPage = 1; // Reset to first page
    this.selectedInboxItemId = null; // Clear selected item when searching
    this.selectedMessage = null; // Clear message details when searching
    this.resetAllPagination();
    this.loadActiveTabMessages();
  }

  // Clear search
  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
    this.applySearch('');
  }

  // Handle new message button
  onNewMessage(): void {
    console.log('this.priorityOptions', this.priorityOptions);
    // Create empty message data for new message
    const newMessageData: NewMessageData = {
      recipients: [],
      priority: this.priorityOptions.length > 0 ? this.priorityOptions[0].id : '1',
      subject: '',
      message: ''
    };
 
    // Open new message modal with empty data
    this.openNewMessageModal(newMessageData, 'new');
  }

  // Handle new message modal close
  onNewMessageClose(): void {
    this.showNewMessageModal = false;
    this.prepopulatedMessageData = null;
    this.messageModalMode = 'new';
    this.editingMessageId = null; // Reset editing message ID
    this.selectedMessage = null; // Clear message details view
    this.selectedInboxItemId = null; // Clear selected item
  }

  // Handle new message send
  onNewMessageSend(messageData: NewMessageData): void {
    console.log('message Data',messageData,)
    // Check if we're editing an existing draft message
    if (this.editingMessageId && this.messageModalMode === 'draft') {
      this.updateDraftMessage(messageData, 'New');
      return;
    }
    
    if (!messageData.recipients || messageData.recipients.length === 0) {
      this.alertService.error('Please select at least one recipient');
      return;
    }

    if (!messageData.subject || !messageData.subject.trim()) {
      this.alertService.error('Please enter a subject for the message');
      return;
    }

    if (!messageData.message || !messageData.message.trim()) {
      this.alertService.error('Please enter a message content');
      return;
    }

    // Get current user ID for sender
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      this.alertService.error('Unable to identify current user. Please log in again.');
      return;
    }

    // Use messageType from messageData if provided, otherwise determine from modal mode
    let messageType: MessageType = messageData.messageType || 'New';
    if (!messageData.messageType) {
      if (this.messageModalMode === 'reply') {
        messageType = 'Reply';
      } else if (this.messageModalMode === 'forward') {
        messageType = 'Forward';
      }
    }

    // Create single payload with all recipients
    const createMessageData = {
      applicationId: parseInt(this.currentApplicationId), // Using applicationId as caseId for now
      senderId: currentUserId, // Add current logged-in user as sender
      subject: messageData.subject.trim(),
      message: messageData.message.trim(),
      priority: messageData.priority,
      attachments : messageData.attachments,
      recipients: messageData.recipients.map(recipient => {
        // Ensure recipient ID is a valid numeric ID, not a string name
        let recipientId: number | null = null;
        
        // Try to parse the ID as a number first
        if (recipient.id && recipient.id !== 'undefined' && recipient.id !== 'null') {
          const parsedId = this.safeParseInt(recipient.id);
          if (parsedId !== null) {
            recipientId = parsedId;
          }
        }
        
        // If parsing failed, try to find the user by name in caseUsers
        if (recipientId === null) {

          // Try exact name match first
          let matchingUser = this.caseUsers.find(user => user.name === recipient.name);
          
          // If no exact match, try partial name match
          if (!matchingUser) {
            matchingUser = this.caseUsers.find(user => 
              user.name.toLowerCase().includes(recipient.name.toLowerCase()) ||
              recipient.name.toLowerCase().includes(user.name.toLowerCase())
            );
          }
          
          // If still no match, try matching by initials or partial name
          if (!matchingUser) {
            const recipientInitials = this.getInitials(recipient.name);
            matchingUser = this.caseUsers.find(user => {
              const userInitials = this.getInitials(user.name);
              return userInitials === recipientInitials || 
                     user.name.toLowerCase().includes(recipientInitials.toLowerCase());
            });
          }
          
          if (matchingUser) {
            const parsedUserId = this.safeParseInt(matchingUser.id);
            if (parsedUserId !== null) {
              recipientId = parsedUserId;
            } else {
              console.warn(`Found matching user but invalid ID: ${matchingUser.name} -> ID: ${matchingUser.id}`);
            }
          } else {
            console.warn(`No matching user found for: ${recipient.name}`);
          }
        }
        
        return {
          id: recipientId.toString(), // Convert back to string for API compatibility
          name: recipient.name,
          role: recipient.role,
          email: recipient.email
        };
      }),
      messageType: messageType,
      parentMessageId: messageData.parentMessageId, // Include parent message ID for threading
      templateName: "Create Message Email",
      templateParams:{},
      notificationChannels:['email']
    };


    // Validate that we have at least one recipient
    if (!createMessageData.recipients || createMessageData.recipients.length === 0) {
      console.error('No valid recipients found for message');
      this.alertService.error('No valid recipients found. Please check recipient selection.');
      return;
    }
    const priorityValue = this.priorityOptions.find(item => item.id === messageData.priority)?.value;
    
    // Send single message with all recipients
    this.InAppMessageService.sendMessage(createMessageData).subscribe({
      next: (response) => {
        this.alertService.success('Message sent successfully.');
        // this.alertService.success(`Message sent successfully to ${messageData.recipients?.map(r => r.name).join(', ')} with ${priorityValue} priority`);
        this.showNewMessageModal = false;
        this.loadMessages(); // Refresh messages
      },
      error: (error) => {
        console.error('Error sending message:', error);
        this.alertService.error('Error sending message. Please try again.');
      }
    });
  }

  // Update existing draft message
  private updateDraftMessage(messageData: NewMessageData, newMessageType?: MessageType): void {
    
    if (!this.editingMessageId) {
      console.error('No editing message ID found');
      return;
    }

    const messageId = this.safeParseInt(this.editingMessageId);
    if (messageId === null) {
      console.error('Invalid message ID for editing:', this.editingMessageId);
      return;
    }

    // Get current user ID for sender
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      this.alertService.error('Unable to identify current user. Please log in again.');
      return;
    }

    // Prepare update data
    const updateData = {
      messageId:messageId,
      applicationId: parseInt(this.currentApplicationId),
      subject: messageData.subject.trim(),
      messageContent: messageData.message.trim(),
      priority: messageData.priority,
      messageType: newMessageType || messageData.messageType || 'Draft',
      attachments: messageData.attachments,
      senderId: currentUserId, // Include sender ID to ensure proper authorization
      recipients: messageData.recipients.map(recipient => {
        // Ensure recipient ID is a valid numeric ID, not a string name
        let recipientId: number | null = null;
        
        // Try to parse the ID as a number first
        if (recipient.id && recipient.id !== 'undefined' && recipient.id !== 'null') {
          const parsedId = this.safeParseInt(recipient.id);
          if (parsedId !== null) {
            recipientId = parsedId;
          }
        }
        
        // If parsing failed, try to find the user by name in caseUsers
        if (recipientId === null) {
          console.warn(`Invalid recipient ID: ${recipient.id}, searching by name: ${recipient.name}`);
          
          // Try exact name match first
          let matchingUser = this.caseUsers.find(user => user.name === recipient.name);
          
          // If no exact match, try partial name match
          if (!matchingUser) {
            matchingUser = this.caseUsers.find(user => 
              user.name.toLowerCase().includes(recipient.name.toLowerCase()) ||
              recipient.name.toLowerCase().includes(user.name.toLowerCase())
            );
          }
          
          // If still no match, try matching by initials or partial name
          if (!matchingUser) {
            const recipientInitials = this.getInitials(recipient.name);
            matchingUser = this.caseUsers.find(user => {
              const userInitials = this.getInitials(user.name);
              return userInitials === recipientInitials || 
                     user.name.toLowerCase().includes(recipientInitials.toLowerCase());
            });
          }
          
          if (matchingUser) {
            const parsedUserId = this.safeParseInt(matchingUser.id);
            if (parsedUserId !== null) {
              recipientId = parsedUserId;
            } else {
              console.warn(`Found matching user but invalid ID: ${matchingUser.name} -> ID: ${matchingUser.id}`);
            }
          } else {
            console.warn(`No matching user found for: ${recipient.name}`);
          }
        }
        
        return {
          id: recipientId.toString(),
          name: recipient.name,
          role: recipient.role,
          email: recipient.email
        };
      })
    };


    // Update the message
    this.InAppMessageService.updateMessage(messageId, updateData).subscribe({
      next: (response) => {
        const action = newMessageType === 'New' ? 'sent' : 'updated';
        this.alertService.success(`Message ${action} successfully`);
        
        // Clear data first, then close modal to ensure form reset
        this.editingMessageId = null;
        this.prepopulatedMessageData = null; // Clear prepopulated data
        this.messageModalMode = 'new'; // Reset modal mode
        this.selectedMessage = null; // Clear message details view
        this.selectedInboxItemId = null; // Clear selected item
        
        // Close modal after clearing data
        this.showNewMessageModal = false;
        
        this.loadMessages(); // Refresh messages
      },
      error: (error) => {
        console.error('Error updating message:', error);
        this.alertService.error('Error updating message. Please try again.');
      }
    });
  }

  // Handle save as draft
  onNewMessageSaveAsDraft(messageData: NewMessageData): void {
    
    // Check if we're editing an existing draft message
    if (this.editingMessageId && this.messageModalMode === 'draft') {
      this.updateDraftMessage(messageData, 'Draft');
      return;
    }
    
    // Get current user ID for sender
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      this.alertService.error('Unable to identify current user. Please log in again.');
      return;
    }

    // Create single payload with all recipients
    const createMessageData = {
      applicationId: parseInt(this.currentApplicationId),
      senderId: currentUserId,
      subject: messageData.subject.trim(),
      message: messageData.message.trim(),
      priority: messageData.priority,
      attachments: messageData.attachments,
      recipients: messageData.recipients.map(recipient => {
        // Ensure recipient ID is a valid numeric ID, not a string name
        let recipientId: number | null = null;
        
        // Try to parse the ID as a number first
        if (recipient.id && recipient.id !== 'undefined' && recipient.id !== 'null') {
          const parsedId = this.safeParseInt(recipient.id);
          if (parsedId !== null) {
            recipientId = parsedId;
          }
        }
        
        // If parsing failed, try to find the user by name in caseUsers
        if (recipientId === null) {
          console.warn(`Invalid recipient ID: ${recipient.id}, searching by name: ${recipient.name}`);
          
          // Try exact name match first
          let matchingUser = this.caseUsers.find(user => user.name === recipient.name);
          
          // If no exact match, try partial name match
          if (!matchingUser) {
            matchingUser = this.caseUsers.find(user => 
              user.name.toLowerCase().includes(recipient.name.toLowerCase()) ||
              recipient.name.toLowerCase().includes(user.name.toLowerCase())
            );
          }
          
          // If still no match, try matching by initials or partial name
          if (!matchingUser) {
            const recipientInitials = this.getInitials(recipient.name);
            matchingUser = this.caseUsers.find(user => {
              const userInitials = this.getInitials(user.name);
              return userInitials === recipientInitials || 
                     user.name.toLowerCase().includes(recipientInitials.toLowerCase());
            });
          }
          
          if (matchingUser) {
            const parsedUserId = this.safeParseInt(matchingUser.id);
            if (parsedUserId !== null) {
              recipientId = parsedUserId;
            } else {
              console.warn(`Found matching user but invalid ID: ${matchingUser.name} -> ID: ${matchingUser.id}`);
            }
          } else {
            console.warn(`No matching user found for: ${recipient.name}`);
          }
        }
        
        return {
          id: recipientId.toString(),
          name: recipient.name,
          role: recipient.role,
          email: recipient.email
        };
      }),
      messageType: 'Draft' as MessageType,
      parentMessageId: messageData.parentMessageId
    };


    // Send draft message
    this.InAppMessageService.sendMessage(createMessageData).subscribe({
      next: (response) => {
        this.alertService.success('Draft saved successfully');
        this.showNewMessageModal = false;
        this.loadMessages(); // Refresh messages
      },
      error: (error) => {
        console.error('Error saving draft:', error);
        this.alertService.error('Error saving draft. Please try again.');
      }
    });
  }

  
 

  // Handle inbox item click
  onInboxItemClicked(item: InboxItem): void {
 
    this.selectedInboxItemId = item.id;
    
    // Mark message as read if it's currently unread
    if (item.isNew && !item.isRead) {
      this.markMessageAsRead(item);
    } else if (!item.isRead) {
      // Fallback: if not marked as new but still unread, mark as read
      this.markMessageAsRead(item);
    } 
    
    // Load message details using common function with safe parsing
    const messageId = this.safeParseInt(item.id);
    if (messageId !== null) {
      this.loadMessageDetails(messageId, 'inbox', item);
    } else {
      console.error('Invalid message ID:', item.id);
      this.alertService.error('Invalid message ID. Please try again.');
    }
  }

  // Mark message as read
  private markMessageAsRead(item: InboxItem): void {
     const messageId = this.safeParseInt(item.id);
    if (messageId === null) {
      console.error('Invalid message ID for marking as read:', item.id);
      return;
    }

    // Get recipient ID from the item
    if (!item.recipientId) {
      console.error('Recipient ID not found for marking message as read');
      return;
    }

    // Update local state immediately for better UX
    item.isNew = false;
    item.isRead = true;
    item.readAt = new Date();

    // Call API to mark as read with both message ID and recipient ID
    this.InAppMessageService.markAsRead(messageId, item.recipientId).subscribe({
      next: (response) => {
        // Archive successful, refresh messages
    //    this.loadMessages();
      },
      error: (error) => {
        console.error('Error marking message as read:', error);
        // Revert local state if API call failed
        item.isNew = true;
        item.isRead = false;
        item.readAt = undefined;
      }
    });
  }

  // Handle message reply
  onMessageReply(message: MessageDetails): void {
     
    // Try to find the sender in caseUsers to get proper numeric ID
    const senderUser = this.caseUsers.find(user => user.name === message.sender.name);
    let senderId: string;
    
    if (senderUser) {
      // Ensure we have a valid numeric ID
      const parsedSenderId = this.safeParseInt(senderUser.id);
      if (parsedSenderId !== null) {
        senderId = parsedSenderId.toString();
      } else {
        console.warn(`Invalid sender ID in caseUsers: ${senderUser.id}, using name as fallback`);
        senderId = message.sender.name;
      }
    } else {
      console.warn(`Sender not found in caseUsers: ${message.sender.name}, using name as fallback`);
      senderId = message.sender.name;
    }
    
    // Prepare reply data with parent message ID for threading and include attachments
    const replyData: NewMessageData = {
      recipients: [{
        id: message.sender.id, // Use proper numeric ID if found, otherwise use name
        name: message.sender.name,
        role: message.sender.role,
        email:  message.sender.email, // Get email from caseUsers if available
        initials: message.sender.initials
      }],
      priority: message.priority,
      subject: `Re: ${message.subject?.replace(/^re:\s*/i, '').trim()}`,
      message: `\n\n------------------------------------------------------------------------------------------------\n--- Original Message ---\nFrom: ${message.sender.name}\nDate: ${message.date} ${message.time}\nSubject: ${message.subject}\n\n${message.content}`,
      parentMessageId: message.parentMessageId || parseInt(message.id), // Set parent message ID for threading
      attachments: message.attachments && message.attachments.length > 0 ? message.attachments : [] // Include attachments from original message
    };
    
    // Open new message modal with reply data
    this.openNewMessageModal(replyData, 'reply');
  }

  // Execute enhanced reply flow: refresh messages, select latest, open details, trigger reply
  async executeEnhancedReplyFlow(): Promise<void> {
    
    try {
      // Step 1: Wait for messages to load completely
      await this.loadMessagesAndWait();
      
      // Step 2: Execute the flow after messages are loaded
      await this.selectLatestMessageAndReply();
      
    } catch (error) {
      console.error('Error in enhanced reply flow:', error);
    }
  }

  // Load messages and wait for completion
  private loadMessagesAndWait(): Promise<void> {
    return new Promise((resolve, reject) => {
      
      // Load inbox messages and wait for completion
      this.loadInboxMessagesAndWait().then(() => {
       
        resolve();
      }).catch((error) => {
        console.error('Error loading inbox messages:', error);
        reject(error);
      });
    });
  }

  // Load inbox messages and return a promise
  private loadInboxMessagesAndWait(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Reset pagination and clear existing messages
      this.inboxPagination.currentPage = 1;
      this.inboxPagination.hasMore = true;
      this.inboxMessages = [];
      this.inboxPagination.isLoading = true;
      this.isLoadingInbox = true;
      
      const params = this.buildMessageQueryParams();
      if (!params) {
        this.isLoadingInbox = false;
        this.inboxPagination.isLoading = false;
        reject(new Error('Invalid query parameters'));
        return;
      }

      // Add pagination parameters
      params.page = this.inboxPagination.currentPage;
      params.limit = this.inboxPagination.pageSize;
      params.messageView = 'INBOX'

      this.InAppMessageService.getInboxMessages(params).subscribe({
        next: (response: any) => {
          const newMessages = response.data?.data || response.data || [];
          const totalCount = response.data?.count || response.count || 0;
          const totalPages = response.data?.totalPages || 0;
          
          this.inboxMessages = newMessages;
          this.inboxPagination.totalCount = totalCount;
          this.inboxPagination.totalPages = totalPages;
          this.tabs[0].count = totalCount;
          
          // Use totalPages to determine if there are more messages
          this.inboxPagination.hasMore = this.inboxPagination.currentPage < totalPages;
          this.inboxPagination.currentPage++;
          
          this.isLoadingInbox = false;
          this.inboxPagination.isLoading = false;
          this.updateCurrentInboxItems();
          
          resolve();
        },
        error: (error) => {
          console.error('Error loading inbox messages:', error);
          this.inboxMessages = [];
          this.tabs[0].count = 0;
          this.inboxPagination.totalCount = 0;
          this.inboxPagination.totalPages = 0;
          this.inboxPagination.hasMore = false;
          this.isLoadingInbox = false;
          this.inboxPagination.isLoading = false;
          this.updateCurrentInboxItems();
          reject(error);
        }
      });
    });
  }

  // Select latest message and trigger reply
  async selectLatestMessageAndReply(): Promise<void> {

    // Ensure we're on the inbox tab
    if (this.activeTab !== 'inbox') {
      this.switchTab('inbox');
      // Wait for tab switch to complete
      await this.waitForTabSwitch();
    }
    
    // Get the latest message from inboxMessages
    if (this.inboxMessages && this.inboxMessages.length > 0) {
      const latestMessage = this.inboxMessages[0]; // First message is usually the latest
      
      // Convert to InboxItem format
      const currentUserId = this.getCurrentUserId();
      if (!currentUserId) {
        console.error('Current user ID not available for marking message as read');
        throw new Error('Current user ID not available');
      }
      
      const latestInboxItem: InboxItem = {
        id: latestMessage.id.toString(),
        avatar: '',
        senderName: latestMessage.sender ? `${latestMessage.sender.firstname} ${latestMessage.sender.lastname}` : 'Unknown',
        role: 'User',
        subject: latestMessage.subject,
        description: latestMessage.messageContent,
        timestamp: latestMessage.insertedOn ? new Date(latestMessage.insertedOn).toISOString() : new Date().toISOString(),
        isNew: false,
        isRead: false, // Mark as unread so it gets marked as read when clicked
        priority: latestMessage.priority || 'Normal',
        recipientId: currentUserId // Use current user ID as recipient (the person who received the message)
      };
      
      
      // Step 3: Select the latest message and wait for details to load
      // Note: This will automatically mark the message as read via onInboxItemClicked()
      await this.selectMessageAndWaitForDetails(latestInboxItem);
      
      // Step 4: Trigger reply after message details are loaded
      if (this.selectedMessage) {
        this.onMessageReply(this.selectedMessage);
      } else {
      }
      
    } else {
      throw new Error('No inbox messages available');
    }
  }

  // Wait for tab switch to complete
  private waitForTabSwitch(): Promise<void> {
    return new Promise((resolve) => {
      const checkTab = () => {
        if (this.activeTab === 'inbox') {
          resolve();
        } else {
          setTimeout(checkTab, 100);
        }
      };
      checkTab();
    });
  }

  // Select message and wait for details to load
  private selectMessageAndWaitForDetails(inboxItem: InboxItem): Promise<void> {
    alert('here in this function')
    return new Promise((resolve, reject) => {

      // Clear any existing selected message
      this.selectedMessage = null;
      
      // Click on the message (this will mark it as read if unread)
      this.onInboxItemClicked(inboxItem);
      
      // Wait for message details to load
      const checkMessageDetails = () => {
        if (this.selectedMessage) {
          resolve();
        } else {
          // Check if we've been waiting too long
          setTimeout(checkMessageDetails, 200);
        }
      };
      
      // Start checking after a short delay
      setTimeout(checkMessageDetails, 100);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        if (!this.selectedMessage) {
          reject(new Error('Timeout waiting for message details'));
        }
      }, 10000);
    });
  }

  // Handle message forward
  onMessageForward(message: MessageDetails): void {
     
    // Prepare forward data and include attachments
    const forwardData: NewMessageData = {
      recipients: [], // Empty for forward - user will select recipients
      priority: message.priority,
      subject: `Fwd: ${message.subject}`,
      message: `\n\n--- Forwarded Message ---\nFrom: ${message.sender.name}\nDate: ${message.date} ${message.time}\nSubject: ${message.subject}\n\n${message.content}`,
      attachments: message.attachments && message.attachments.length > 0 ? message.attachments : [] // Include attachments from original message
    };
    
    // Open new message modal with forward data
    this.openNewMessageModal(forwardData, 'forward');
  }

  // Handle message edit (for draft messages)
  onMessageEdit(message: MessageDetails): void {
     
    // Only allow editing draft messages
    if ((message.messageType ).toLowerCase() !== 'draft') {
      console.warn('Only draft messages can be edited');
      return;
    }
    
    // Set the message being edited
    this.editingMessageId = message.id;
    
    // Prepare data for editing
    const editData: NewMessageData = {
      subject: message.subject,
      message: message.content,
      recipients: message.recipients || [],
      priority: message.priority,
      attachments: message.attachments || [],
      messageType: 'Draft' as MessageType
    };
    
    this.openNewMessageModal(editData, 'draft');
  }

  // Handle message archive
  onMessageArchive(message: MessageDetails): void {
    if (!message || !message.id) {
      console.error('Invalid message for archiving');
      return;
    }

    const messageId = this.safeParseInt(message.id);
    if (messageId === null) {
      console.error('Invalid message ID for archiving:', message.id);
      this.alertService.error('Invalid message ID. Cannot archive message.');
      return;
    }

    // Show confirmation dialog
      // Get current user ID from auth service or session
      const currentUserId = this.getCurrentUserId();
      
      this.InAppMessageService.archiveMessage(messageId, currentUserId ?? undefined).subscribe({
        next: (response) => {
          this.alertService.success('Message archived successfully');
          this.selectedMessage = null; // Clear selected message
          this.selectedInboxItemId = null; // Clear selected item
          this.loadMessages(); // Refresh messages to update counts
        },
        error: (error) => {
          console.error('Error archiving message:', error);
          this.alertService.error('Error archiving message. Please try again.');
        }
      });
  }

  // Open new message modal with pre-filled data
  private openNewMessageModal(messageData: NewMessageData, mode: ModalMode): void {
    this.messageModalMode = mode;
    this.showNewMessageModal = true;
    console.log('Opening new message modal with data:', messageData, 'Mode:', mode);
    const currentUserRole = this.getCurrentUserRole();
    if (mode === 'new' && this.isApplicant && currentUserRole !== 'Educator') {
      const currentUserRecipient = this.getCurrentUserAsRecipient();
      if (currentUserRecipient) {
        this.caseUsers = [currentUserRecipient];
        this.prepopulatedMessageData = { ...messageData, recipients: [] };
      } else {
        this.caseUsers = [];
        this.prepopulatedMessageData = messageData;
      }
      return;
    }
    this.prepopulatedMessageData = mode === 'new' && currentUserRole === 'Educator'
      ? { ...messageData, recipients: [] }
      : messageData;
    this.caseUsers = [];
    this.loadCaseUsers();
  }

 

  // Handle sent item click
  onSentItemClicked(item: SentItem): void {
     this.selectedInboxItemId = item.id;
    
    // Load message details using common function with safe parsing
    const messageId = this.safeParseInt(item.id);
    if (messageId !== null) {
      this.loadMessageDetails(messageId, 'sent', item);
    } else {
      console.error('Invalid message ID:', item.id);
      this.alertService.error('Invalid message ID. Please try again.');
    }
  }


  // Handle draft item click
  onDraftItemClicked(item: DraftItem): void {
     this.selectedInboxItemId = item.id;
    
    // Load message details using common function with safe parsing
    const messageId = this.safeParseInt(item.id);
    if (messageId !== null) {
      this.loadMessageDetails(messageId, 'draft', item);
    } else {
      console.error('Invalid message ID:', item.id);
      this.alertService.error('Invalid message ID. Please try again.');
    }
  }

  // Handle archived item click
  onArchivedItemClicked(item: ArchiveItem): void {
    this.selectedInboxItemId = item.id;
    
    // Load message details using common function with safe parsing
    const messageId = this.safeParseInt(item.id);
    if (messageId !== null) {
      this.loadMessageDetails(messageId, 'archive', item);
    } else {
      console.error('Invalid message ID:', item.id);
      this.alertService.error('Invalid message ID. Please try again.');
    }
  }

  // Handle draft edit
  onDraftEdit(item: DraftItem): void {
     // TODO: Open draft editor
    this.alertService.info(`Edit draft to ${item.recipientName}`);
  }


  // Load case users from API
  async loadCaseUsers(): Promise<void> {
    try {
      const entity_Id = this.currentApplicationId;
      const entity_Type = 'application';
      this.InAppMessageService.loadCaseUsersWithFallback(entity_Type, entity_Id).subscribe({
        next: (recipients) => {
          this.caseUsers = recipients.map(recipient => ({
            id: recipient.id,
            name: recipient.name,
            role: recipient.role,
            role_id: recipient.role_id,
            email: recipient.email || null,
            avatar: recipient.avatar || null,
            initials: recipient.initials || this.getInitials(recipient.name),
            is_application_creator: recipient.is_application_creator
          }));
          if (this.messageModalMode === 'new' && this.showNewMessageModal && this.prepopulatedMessageData) {
            const currentUserRole = this.getCurrentUserRole();
            // Educator should choose a recipient manually.
            if (currentUserRole !== 'Educator') {
              const applicantUser = this.caseUsers.find(u => u.role_id === 405);
              const userToSelect = applicantUser || this.caseUsers.find(u => u.is_application_creator);
              if (userToSelect) {
                this.prepopulatedMessageData = {
                  ...this.prepopulatedMessageData,
                  recipients: [userToSelect]
                };
              }
            }
          }
        },
        error: (error) => {
          console.error('Error loading case users:', error);
        },
        complete: () => {
          this.isLoadingCaseUsers = false;
        }
      });
    } catch (error) {
      console.error('Error in loadCaseUsers:', error);
      this.isLoadingCaseUsers = false;
    }
  }

  

  // Helper method to generate initials from full name
  private getInitials(fullName: string): string {
    if (!fullName) return '';
    
    const names = fullName.split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
    } else if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return '';
  }

  // Safe parseInt method to prevent NaN errors
  private safeParseInt(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    const parsed = parseInt(value.toString(), 10);
    return isNaN(parsed) ? null : parsed;
  }
 
 



  // Refresh case users (can be called when needed)
  async refreshCaseUsers(): Promise<void> {
    await this.loadCaseUsers();
    await this.loadPriorityData();
  }

  // Set current application ID (should be called when case context changes)
  setCurrentApplication(applicationId: string): void {
    this.currentApplicationId = applicationId;
    this.loadCaseUsers(); // Reload users for the new application
    this.loadMessages(); // Reload messages for the new application
    this.loadPriorityData();
  }

  // Handle application ID changes from route parameters
  private handleApplicationIdChange(newApplicationId: number): void {
    if (newApplicationId) {
      // Check if this is a new application ID (different from current) or initial load
      const isNewApplication = newApplicationId !== this.currentApplicationId;
      const isInitialLoad = !this.currentApplicationId;
      
      // Update current application ID
      this.currentApplicationId = newApplicationId;
      
      if (isNewApplication || isInitialLoad) {
        // Reset UI state for new application
        this.currentPage = 1; // Reset pagination
        this.searchQuery = ''; // Clear search
        this.selectedInboxItemId = null; // Clear selection
        this.selectedMessage = null; // Clear message details
        
        // Clear existing message data
        this.clearAllMessageData();
        
        // Reset pagination for all tabs
        this.resetAllPagination();
        
        // Load initial tab data
        this.loadTabData();
        
        // Reload data for the new application
        this.loadCaseUsers();
        this.loadPriorityData();
        this.loadMessages();
      }
    }
  }

  // Clear all message data when switching applications
  private clearAllMessageData(): void {
    // Clear API message data
    this.inboxMessages = [];
    this.sentMessages = [];
    this.draftMessages = [];
    this.archivedMessages = [];
    
    // Clear current display data
    this.currentInboxItems = [];
    this.currentSentItems = [];
    this.currentDraftItems = [];
    this.currentArchivedItems = [];
    this.currentData = [];
    
    // Clear message details
    this.messageDetailsData = {};
    this.sentItemsData = [];
    this.draftItemsData = [];
    
    // Reset tab counts
    this.tabs.forEach(tab => tab.count = 0);
    
    // Reset unread count
    this.unreadCount = 0;
  }

  // Reset pagination for all tabs
  private resetAllPagination(): void {
    // Reset inbox pagination
    this.inboxPagination = {
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      hasMore: true,
      isLoading: false,
      hasScrolled: false
    };
    
    // Reset sent pagination
    this.sentPagination = {
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      hasMore: true,
      isLoading: false,
      hasScrolled: false
    };
    
    // Reset draft pagination
    this.draftPagination = {
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      hasMore: true,
      isLoading: false,
      hasScrolled: false
    };
    
    // Reset archive pagination
    this.archivePagination = {
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      hasMore: true,
      isLoading: false,
      hasScrolled: false
    };
  }

  

  // Common function to load message details
  private loadMessageDetails(messageId: number, messageType: 'inbox' | 'sent' | 'draft' | 'archive', item?: any): void {
     
    // Show loading state if needed
    // this.isLoadingMessageDetails = true;
    
    this.InAppMessageService.getMessageById(messageId).subscribe({
      next: (response: any) => {
        // Handle nested response structure - extract the actual message data
        const message = response.data || response;
        console.log('Raw message details response:', message);
        // Get sender role directly from the sender object (now populated by API)
        let senderRole = 'User'; // Default role
        if (message?.sender?.role) {
          senderRole = message.sender.role;
        }
        console.log('message in in-app-message.Component', message)
        const timestamp = message?.updatedAt ?? message?.insertedOn;
        // Transform message to MessageDetails format with proper null checks
        this.selectedMessage = {
          id: message?.id ? message.id.toString() : '0', // Safe conversion with fallback
          subject: message?.subject,
          content: message?.messageContent,
          sender: { 
            name: message?.sender ? `${message.sender.firstname || ''} ${message.sender.lastname || ''}`.trim() || 'Unknown' : 'Unknown',
            role: senderRole, // Use actual role from API
            id: message.sender.id.toString(),
            email: message.sender.email,
            initials: this.getInitials(message?.sender ? `${message.sender.firstname || ''} ${message.sender.lastname || ''}`.trim() || 'Unknown' : 'Unknown')
          },
          recipient: message?.recipients && message.recipients.length > 0 ? message.recipients.map(r => r.name || 'Unknown').join(', ') : 'Unknown',
          recipients: message?.recipients || [], // Include full recipients array
          messageType: messageType, // Capitalize first letter for conditional display
          date: message?.insertedOn ? new Date(timestamp).toLocaleDateString() : 'Unknown Date',
          time: message?.insertedOn ? new Date(timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'Unknown Time',
          priority: message?.priority ,
          priorityDisplay: message?.priorityDisplay || 'Medium',
          parentMessageId: message?.parentMessageId || undefined, // Capture parent message ID for threading
          messagetime: timestamp,
          attachments: message?.attachments || [] // Include attachments from API response
        };
      
      },
      error: (error) => {
        console.error(`Error loading ${messageType} message details:`, error);
        this.selectedMessage = null;
        // Show error message to user if needed
        // this.showErrorMessage('Failed to load message details');
      }
    });
  }

  // Fetch message recipients from API
  private fetchMessageRecipients(messageId: number, callback: (recipients: any[]) => void): void {
    this.InAppMessageService.getMessageRecipients(messageId).subscribe({
      next: (recipients) => {
         callback(recipients);
      },
      error: (error) => {
        console.error(`Error fetching recipients for message ${messageId}:`, error);
        callback([]);
      }
    });
  }

  onDraftDelete(item: DraftItem) {
    console.log('Delete draft item:', item);
    if (!item || !item.id) {
      console.error('Invalid draft item for deletion');
      return;
    }
    const messageId = this.safeParseInt(item.id);
    if (messageId === null) {
      console.error('Invalid message ID for deletion:', item.id);
      this.alertService.error('Invalid message ID. Cannot delete draft.');
      return;
    }
    this.InAppMessageService.softDeleteMessage(messageId).subscribe({
      next: () => {
        console.log('Draft item deleted successfully:', item);
        this.draftMessages = this.draftMessages.filter(message => message.id?.toString() !== item.id);
        this.currentDraftItems = this.currentDraftItems.filter(draftItem => draftItem.id !== item.id);
        this.draftPagination.totalCount = Math.max(0, this.draftPagination.totalCount - 1);
        this.tabs[2].count = this.draftPagination.totalCount;

        if (this.selectedInboxItemId === item.id) {
          this.selectedInboxItemId = null;
          this.selectedMessage = null;
        }

        this.alertService.success('Draft deleted successfully');
      },
      error: (error) => {
        console.error('Error deleting draft item:', error);
        this.alertService.error('Error deleting draft. Please try again.');
      }
    });
  }

  onMessageUnarchive(message: MessageDetails): void {
    if (!message || !message.id) {
      console.error('Invalid message for unarchiving');
      this.alertService.error('Invalid message. Cannot unarchive.');
      return;
    }

    const messageId = this.safeParseInt(message.id);

    if (messageId === null || isNaN(messageId)) {
      console.error('Invalid message ID for unarchiving:', message.id);
      this.alertService.error('Invalid message ID. Cannot unarchive message.');
      return;
    }
    const currentUserId = this.getCurrentUserId();

    this.InAppMessageService.unArchiveMessage(messageId, currentUserId ?? undefined).subscribe({
      next: () => {
        this.alertService.success('Message unarchived successfully');
        this.selectedMessage = null;
        this.selectedInboxItemId = null;
        this.loadMessages();
      },
      error: (error) => {
        console.error('Error unarchiving message:', error);

        const errorMessage =
          error?.error?.message ||
          'Error unarchiving message. Please try again.';

        this.alertService.error(errorMessage);
      }
    });
  }

  getCurrentUserRole() {
    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const role = currentUser?.userroles?.[0]?.name || null;
    return role;
  }

  ngOnDestroy(): void {
    this.orgSubs.unsubscribe();
    this.socketSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }
}